<script setup lang="ts">

import { ref, computed, watch, onMounted, onUnmounted } from "vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import axios from "axios"
import { useToast } from "@/components/ui/toast"
import { useI18n } from 'vue-i18n';
import SingleSelect from './SingleSelect.vue'
import ManySelect from './ManySelect.vue'
import TiptapEditor from './TiptapEditor.vue'
import StandortSelect from '@/components/StandortSelect.vue'

import { optionLabel, capitalize, sortDropdownOptions, dropdownSortConfigForColumn } from '@/utils/globals'
import { Check, Loader2, X } from 'lucide-vue-next'

interface ColumnDef {
  key: string
  label: string
  type?: "text" | "number" | "date" | "color" | "select" | "boolean" | "multi-select" | "many" | "file" | "datetime"
  edit?: string
  write?: boolean
  required?: boolean
  options?: any[]
  /** Premium RG: full legal status labels for display; `options` stays basic for editing. */
  display_options?: any[]
  optionsEndpoint?: string
  width?: number | string
}

const { toast } = useToast()

const { t, locale } = useI18n();

// Map i18n locale to date locale string
const dateLocale = computed(() => {
  const localeMap: Record<string, string> = {
    'de': 'de-DE',
    'en': 'en-US',
    'zh-TW': 'zh-TW'
  }
  return localeMap[locale.value] || 'de-DE'
})

const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
if (token) axios.defaults.headers.common["X-CSRF-Token"] = token


const props = withDefaults(defineProps<{
  columns: ColumnDef[]
  currentItem: Object
  label: string
  labelSingular?: string
  logo?: string | null
  endpoint?: string
  saveendpoint?: string
  endpointpars?: Record<string, number>
  enableSearch?: boolean
  enableAdd?: boolean
  enableEdit?: boolean
  enableDetail?: boolean
  enableDelete?: boolean
  enableFilter?: boolean
  enableColChange?: boolean
  enableColFilterSide?: boolean
  enableColFilterTop?: boolean
  alwaysEdit?: boolean
  withpaging?: boolean
  hideActionColumn?: boolean
  headerFontSize?: number
  singleSelectFontsize?: number
  data?: any[] 
  onDelete?: (id: number) => void
  dynamicOptions?: Record<string, any[]>
  /** When true: view-only modal — no Save, all fields shown read-only when col.write is false. */
  readOnly?: boolean
  /** When true: save request in progress (show spinner, disable). */
  isSaving?: boolean
}>(), {
  readOnly: false,
  isSaving: false,
  saveendpoint: null,
  logo: null,
  enableSearch: false,
  enableAdd: true,
  enableEdit: true,
  enableDetail: true,
  enableDelete: true,
  enableFilter: true,
  enableColChange: true,
  enableColFilterSide: true,
  enableColFilterTop: false,
  alwaysEdit: false,
  withpaging: true,
  hideActionColumn: false,
})


function getNested(obj: any, path: string) {
  let psplit = path.split(".");
  return psplit.reduce((o, k) => (o ? o[k] : undefined), obj)
}

function isBooleanValue(value: any): boolean {
  if (value === true || value === 1 || value === "1" || value === "true" || value === "Ja" || value === "Yes") {
    return true
  }
  return false
}

function setNested(obj: any, path: string, value: any, event?: any) {
  const keys = path.split(".");
  let o = obj;

  keys.slice(0, -1).forEach((k) => {
    if (!o[k]) o[k] = {};
    o = o[k];
  });

  o[keys[keys.length - 1]] = value;
}

const emit = defineEmits<{(e: 'close-editor'): void; (e: 'save-editor'): void;}>();

function standortOptionsForItem(item: any) {
  const standorts = item?.standorts
  if (!Array.isArray(standorts)) return []
  return sortDropdownOptions(standorts.map((s: any) => ({ value: s.id, label: s.displayname || s.name || String(s.id) })))
}

function preserveOptionOrderForColumn(col: any, options: any[] | null = null) {
  return dropdownSortConfigForColumn(col, options).preserveOrder
}

function optionsForColumn(col: any) {
  const options = col?.options || props.dynamicOptions?.[col?.key] || []
  return sortDropdownOptions(options, dropdownSortConfigForColumn(col, options))
}

watch(
  () => (props.currentItem as any)?.standorts,
  (standorts) => {
    // If exactly one Standort exists, default import selection to it.
    if (!Array.isArray(standorts) || standorts.length !== 1) return
    const current = (props.currentItem as any)?.import_standort_id
    if (current !== null && current !== undefined && String(current).trim() !== "") return
    setNested(props.currentItem as any, "import_standort_id", standorts[0].id)
  },
  { immediate: true }
)


function formatISOForDateInput(val: string | undefined | null) {
  if (!val) return ''
  // If val contains a timestamp, strip it
  return val.split('T')[0]
}

function toSelectHash(options, arr, displayOptions = null) {
	console.log("JSON.stringify(options)=" + JSON.stringify(options));
	console.log("JSON.stringify(arr)=" + JSON.stringify(arr));
	if (arr == null || arr == "") {
		return {}
	}
	  return arr.reduce((acc, obj) => {
	    acc[obj.id] = optionLabel(options, obj, displayOptions);
	    return acc;
	  }, {});
}

function toFilterHash(options, arr, displayOptions = null) {
	if (arr == null) {
	    return [];
	} 
	
	console.log("arr=" + JSON.stringify(arr))
	return toSelectHash(options, arr.map(item => ({ id: item })), displayOptions)
}

function toCollection(list) {
  if (list == null) {
	return []
  }
  return Array.isArray(list) ? list : Object.values(list);
}

function toggleManyEdit(col, currentItem, oid, val) {
  let k = col.key;
  let currentValue = getNested(currentItem, k);
  
  // Initialize as empty array if null/undefined
  if (!currentValue) {
    setNested(currentItem, k, []);
    currentValue = [];
  }
  
  if (val) {
    // Add the option - find it from col.options or props.dynamicOptions
    const options = optionsForColumn(col);
    const missingOption = options.find(opt => opt.id === oid);
    if (missingOption) {
      // Check if already exists
      const exists = currentValue.some(item => {
        const itemId = typeof item === 'object' && item !== null ? item.id : item;
        return itemId === oid;
      });
      if (!exists) {
        setNested(currentItem, k, [...currentValue, missingOption]);
      }
    }
  } else {
    // Remove the option
    const filtered = currentValue.filter(item => {
      const itemId = typeof item === 'object' && item !== null ? item.id : item;
      return itemId !== oid;
    });
    setNested(currentItem, k, filtered);
  }
}

const menuVisible = ref(false)
const menuX = ref(0)
const menuY = ref(0)
const selectedCellContent = ref('')
const hoverMenuItem = ref(false)

function showContextMenu(event: MouseEvent, content: string) {
  // Only trigger for secondary button (right click)
  if (event.button !== 2) return
  event.preventDefault()
  event.stopPropagation()

  selectedCellContent.value = content

  // Prevent menu from going outside the viewport
  const menuWidth = 140
  const menuHeight = 36
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  menuX.value = event.clientX + menuWidth > viewportWidth ? viewportWidth - menuWidth - 10 : event.clientX
  menuY.value = event.clientY + menuHeight > viewportHeight ? viewportHeight - menuHeight - 10 : event.clientY

  menuVisible.value = true
}

function hideContextMenu() {
  menuVisible.value = false
}

function copyToClipboard() {
  navigator.clipboard.writeText(selectedCellContent.value).then(() => {
    toast({ title: "Copied", description: selectedCellContent.value })
    hideContextMenu()
  })
}

function onFileChange(event: Event, key: string, currentItem) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0] || null;

  if (file) {
    // Store the file temporarily in your currentItem
    setNested(currentItem, key, file);
  } else {
    // Clear if no file selected
    setNested(currentItem, key, null);
  }
}

// Hide menu when clicking anywhere else
onMounted(() => window.addEventListener('click', hideContextMenu))
onUnmounted(() => window.removeEventListener('click', hideContextMenu))

</script>

<template>
	
	<!-- Dedicated Editor Panel -->
	<div
	  class="fixed inset-0 z-[10001] flex items-start justify-center pt-20"
	  @click.stop
	>
	  <!-- Backdrop -->
	  <div
	    class="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
	    @click="$emit('close-editor')"
	  ></div>

	  <!-- Modal / Panel -->
	  <div
	    class="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[85vh] z-10 flex flex-col animate-in fade-in-50 zoom-in-95"
	  >
	    <!-- Header (sticky, styled) -->
	    <div
	      class="px-6 py-4 border-b bg-gradient-to-r from-gray-50 to-white sticky top-0 z-20 flex justify-between items-center rounded-t-2xl"
	    >
	      <h2 class="text-lg font-semibold text-gray-800">
	        {{ capitalize(props.label) }}
	      </h2>
	      <button
	        @click="$emit('close-editor')"
	        class="text-gray-400 hover:text-gray-600 transition-colors"
	      >
	        ✖
	      </button>
	    </div>

	    <!-- Body (scrollable) -->
	  <div class="editor-panel-copy-scope px-6 py-4 overflow-y-auto flex-1 space-y-0">
	    <div v-if="currentItem">
	      <div class="rounded-xl border border-gray-200 overflow-hidden bg-white">
	        <div
	          v-for="(col, idx) in props.columns.filter(c => c.key !== 'actions' && c.key !== 'buffer')"
	          :key="col.key"
	          class="p-4 space-y-2 transition-colors"
	          :class="idx > 0 ? 'border-t border-gray-200' : ''"
	        >
	          <!-- Field Label -->
	          <label
	            class="block text-sm font-semibold text-gray-900 tracking-wide mb-2"
	          >
	            {{ col.label }}
	          </label>

	        <!-- READONLY FIELDS -->
	          <template v-if="!col.write">
	          <template v-if="col.type === 'boolean' || col.edit === 'checkbox'">
	            <div class="flex items-center">
	              <Check
	                v-if="isBooleanValue(getNested(currentItem, col.key))"
	                class="w-5 h-5 text-green-600"
	              />
	              <X
	                v-else
	                class="w-5 h-5 text-red-600"
	              />
	            </div>
	          </template>

	          <template v-else-if="col.type === 'select' || col.type === 'multi-select'">
	            <div class="text-sm text-gray-700">
	              {{
	                (() => {
	                  const v = getNested(currentItem, col.key)
	                  const opts = optionsForColumn(col)
	                  const fromOpts = opts.find(opt => opt.id == v)
	                  if (fromOpts) return fromOpts.label
	                  const disp = col.display_options || []
	                  const fromDisp = disp.find(opt => opt.id == v)
	                  return fromDisp?.label ?? v
	                })()
	              }}
	            </div>
	          </template>

	          <template v-else-if="col.type === 'many'">
	            <div class="text-sm text-gray-700">
	              {{ toCollection(getNested(currentItem, col.key)).map(u => optionLabel(optionsForColumn(col), u, col.display_options)).join(',') }}
	            </div>
	          </template>

	          <template v-else-if="col.type === 'color'">
	            <div class="flex items-center gap-2">
	              <div class="w-4 h-4 rounded-full border" :style="{ backgroundColor: getNested(currentItem, col.key) }"></div>
	              <span class="text-sm text-gray-600">{{ getNested(currentItem, col.key) }}</span>
	            </div>
	          </template>

	          <template v-else-if="col.type === 'date'">
	            <div class="text-sm text-gray-700">
	              {{
	                getNested(currentItem, col.key)
	                  ? new Date(getNested(currentItem, col.key)).toLocaleDateString(dateLocale, {
	                      day: "2-digit",
	                      month: "2-digit",
	                      year: "numeric",
	                    })
	                  : ""
	              }}
	            </div>
	          </template>

	          <template v-else-if="col.type === 'text' && col.edit === 'richtext'">
	            <div
	              class="richtext prose prose-sm max-w-none text-gray-800 border rounded p-2 max-h-64 overflow-y-auto"
	              v-html="(getNested(currentItem, col.key) as string) || ''"
	            />
	          </template>

	          <template v-else-if="col.type === 'text'">
	                <div
	                  class="whitespace-pre-wrap break-words text-sm text-gray-700"
	                  @contextmenu.prevent="showContextMenu($event, getNested(currentItem, col.key))"
	                >
	                  {{ getNested(currentItem, col.key) }}
	                </div>
	          </template>

	          <template v-else>
	            <div class="text-sm text-gray-700">{{ getNested(currentItem, col.key) }}</div>
	          </template>
	        </template>

	        <!-- WRITABLE FIELDS -->
	        <template v-else>
	          <template v-if="!col.type || (col.type === 'text' && col.edit !== 'text_area' && col.edit !== 'richtext')">
	            <Input
	              :modelValue="getNested(currentItem, col.key)"
	              @update:modelValue="val => setNested(currentItem, col.key, val)"
	              class="w-full"
	            />
	          </template>
	          <template v-else-if="col.edit === 'text_area'">
	            <textarea
	              :value="getNested(currentItem, col.key)"
	              @input="setNested(currentItem, col.key, ($event.target as HTMLTextAreaElement).value)"
	              rows="6"
	              class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
	            ></textarea>
	          </template>
	          <template v-else-if="col.type === 'number'">
	            <input
	              type="number"
	              :value="getNested(currentItem, col.key)"
	              @input="setNested(currentItem, col.key, ($event.target as HTMLInputElement).valueAsNumber)"
	              class="border rounded-lg p-2 text-sm w-full"
	            />
	          </template>
	          <template v-else-if="col.type === 'date'">
	            <input
	              type="date"
	              :value="formatISOForDateInput(getNested(currentItem, col.key))"
	              @input="setNested(currentItem, col.key, ($event.target as HTMLInputElement).value)"
	              class="border rounded-lg p-2 text-sm w-full"
	            />
	          </template>
	          <template v-else-if="col.type === 'boolean' || col.edit === 'checkbox'">
	            <label class="flex items-center gap-2">
	              <input
	                type="checkbox"
	                :checked="isBooleanValue(getNested(currentItem, col.key))"
	                @change="setNested(currentItem, col.key, ($event.target as HTMLInputElement).checked)"
	                class="h-4 w-4"
	              />
	              <span class="text-sm">Yes</span>
	            </label>
	          </template>
			  <template v-else-if="col.type === 'file'">
          <div class="flex flex-col gap-2">
            <div v-if="col.key === 'importfile' && Array.isArray((currentItem as any)?.standorts) && (currentItem as any).standorts.length > 1">
              <div class="text-xs text-gray-500">Standort</div>
              <StandortSelect
                :items="standortOptionsForItem(currentItem as any)"
                @change="value => setNested(currentItem, 'import_standort_id', (value === -1 || value === '-1') ? '' : value)"
              />
            </div>
            <input
              type="file"
              @change="onFileChange($event, col.key, currentItem)"
              class="border rounded-lg p-2 text-sm w-full"
            />
          </div>
			  </template>
			  <template v-else-if="col.type === 'text' && col.edit === 'richtext'">
				<TiptapEditor
				  :model-value="getNested(currentItem, col.key)"
				  @update:model-value="val => setNested(currentItem, col.key, val)"
				/>
			  </template>
			  <template v-else-if="col.type === 'many'">
			    <ManySelect  :col="col"
			      :selectedOptions="toSelectHash(optionsForColumn(col), getNested(currentItem, col.key), col.display_options)"
			      :dynamicOptions="props.dynamicOptions || {}"
			      :toggleOption="(oid, val) => toggleManyEdit(col, currentItem, oid, val)"	/>
			  </template>
	          <template v-else-if="col.type === 'select' || col.type === 'multi-select'">
				<SingleSelect
				  :options="optionsForColumn(col)"
				  :displayOptions="col.display_options"
				  :preserveOrder="preserveOptionOrderForColumn(col)"
				  :modelValue="getNested(currentItem, col.key)"
				  @update:modelValue="val => setNested(currentItem, col.key, val)"
				/>
	          </template>
	        </template>
	        </div>
	      </div>
		  <div class="hidden" style="height: 500px	">
		      <slot name="extra" :item="currentItem" />
		  </div>
	    </div>
	  </div>

	    <!-- Footer (sticky, styled) -->
	    <div class="px-6 py-4 border-t bg-white sticky bottom-0 z-20 flex justify-end gap-3 rounded-b-2xl">
	      <Button
	        v-if="!readOnly"
	        class="bg-blue-600 text-white hover:bg-blue-700"
			@click.stop
	        :disabled="isSaving"
	        @click="$emit('save-editor')"
	      >
	        <Loader2 v-if="isSaving" class="mr-2 h-4 w-4 animate-spin" />
	        {{ isSaving ? t('view.btn_saving') : t('view.btn_save') }}
	      </Button>		  
	      <Button variant="outline" :disabled="isSaving" @click.stop @click="$emit('close-editor')">
		  {{ readOnly ? t('view.close') : t('view.btn_cancel') }}
	      </Button>
	    </div>
	  </div>
	</div>

		<!-- Custom left-click context menu -->
    <div v-if="menuVisible"
    :style="{
    position: 'fixed',
    top: menuY + 'px',
    left: menuX + 'px',
    background: '#fff',
    border: '1px solid #ccc',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    zIndex: 2000,
    minWidth: '120px',
    }"
    >
    <div
      @click="copyToClipboard"
      @mouseover="hoverMenuItem = true"
      @mouseleave="hoverMenuItem = false"
      :style="{ padding: '6px 12px', cursor: 'pointer', backgroundColor: hoverMenuItem ? '#eee' : 'transparent' }"
    >
      {{ t('view.copy') }}
    </div>
	</div>

</template>

<style scoped>
.editor-panel-copy-scope,
.editor-panel-copy-scope :deep(*) {
  -webkit-user-select: text !important;
  user-select: text !important;
}
</style>
